import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Sparkles } from "lucide-react";
import { content } from "../../data/content";
import { burstConfetti } from "../../lib/confetti";
import { scrollToId } from "../../hooks/useLenis";

export function GiftSurprise() {
  const [opened, setOpened] = useState(false);

  const handleOpen = () => {
    if (opened) return;
    setOpened(true);
    burstConfetti();
    setTimeout(() => scrollToId("manifesto"), 1400);
  };

  return (
    <section
      id="gift"
      data-testid="gift-section"
      className="relative min-h-[80vh] w-full bg-cream flex flex-col items-center justify-center px-4 py-24"
    >
      <div className="relative flex flex-col items-center text-center">
        <motion.button
          data-testid="gift-box"
          onClick={handleOpen}
          className="relative outline-none"
          whileHover={{ scale: opened ? 1 : 1.05 }}
          whileTap={{ scale: opened ? 1 : 0.92 }}
          aria-label="Abrir regalo"
        >
          <motion.div
            className="absolute inset-0 rounded-full bg-sun blur-2xl -z-10"
            animate={{ scale: opened ? 1.6 : [1, 1.15, 1], opacity: opened ? 0.7 : 0.4 }}
            transition={{ duration: 2, repeat: opened ? 0 : Infinity, ease: "easeInOut" }}
          />

          <motion.div
            animate={
              opened
                ? { y: -80, rotate: -18, opacity: 0 }
                : { y: [0, -6, 0] }
            }
            transition={
              opened
                ? { duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }
                : { duration: 2, repeat: Infinity, ease: "easeInOut" }
            }
            className="mx-auto w-40 md:w-52 h-12 md:h-16 rounded-lg bg-rose border-[3px] border-ink shadow-brutal relative z-20"
          >
            <div className="absolute left-1/2 -translate-x-1/2 -top-6 w-12 h-8 rounded-t-full border-[3px] border-ink bg-grape" />
          </motion.div>

          <div className="mx-auto -mt-1 w-36 md:w-48 h-32 md:h-40 rounded-b-xl bg-grape border-[3px] border-ink shadow-brutal-lg relative overflow-hidden">
            <div className="absolute left-1/2 -translate-x-1/2 top-0 h-full w-8 md:w-10 bg-sun border-x-[3px] border-ink" />
            <AnimatePresence>
              {opened && (
                <motion.div
                  initial={{ scale: 0, y: 40, opacity: 0 }}
                  animate={{ scale: 1, y: -10, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
                  className="absolute inset-0 flex items-center justify-center z-30"
                >
                  <Sparkles className="w-16 h-16 text-white" strokeWidth={2.5} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.button>

        <AnimatePresence mode="wait">
          {!opened ? (
            <motion.p
              key="hint"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="font-accent text-3xl md:text-5xl text-ink mt-10 flex items-center gap-2"
            >
              <Gift className="w-7 h-7 text-rose" />
              {content.giftHint}
            </motion.p>
          ) : (
            <motion.p
              key="opened"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-accent text-3xl md:text-5xl text-rose mt-10"
            >
              {content.giftOpened}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
